import { Map, List } from 'immutable'
import { mapValues, isNaN, omitBy, isUndefined } from 'lodash'

import DashboardRecord from 'records/Dashboard/dashboard'
import WidgetRecord from 'records/Dashboard/widget'

const string = 'string'
const number = 'number'

const parameterDefinitions = {
  visualization: {
    type: string,
    defaultValue: 'line',
  },
  x: {
    type: number,
    defaultValue: 0,
  },
  y: {
    type: number,
    defaultValue: 0,
  },
  width: {
    type: number,
    defaultValue: 4,
  },
  height: {
    type: number,
    defaultValue: 2,
  },
}

export const calculateNewWidgetPositionY = (widgets) =>
  widgets.reduce(
    (sum, widget) => sum + widget.getIn(['parameters', 'height']),
    0
  )

export const mapDashboard = (dashboard) =>
  new DashboardRecord({
    name: dashboard.name,
    id: String(dashboard.id),
    isOwner: dashboard.isOwner,
    canEdit: dashboard.canEdit,
    dashboardUsers: List(dashboard.dashboardUsers || []),
    dashboardRoles: List(dashboard.dashboardRoles || []),
    description: dashboard.description,
    companyIds: List(
      (dashboard.companyIds || []).map((companyId) => String(companyId))
    ),
    integrationSourceSystemIds: List(
      (dashboard.integrationSourceSystemIds || []).map(
        (integrationSourceSystemId) => String(integrationSourceSystemId)
      )
    ),
    isCustomerDashboard: dashboard.isCustomerDashboard,
    isSystemDashboard: dashboard.isSystemDashboard,
  })

export const mapWidget = ({ widget, dashboardId }) =>
  new WidgetRecord({
    id: String(widget.id),
    dashboardId: String(dashboardId),
    type: widget.type,
    parameters: Map(mapParameters(widget.parameters)),
  })

export const mapParameters = (parameters) =>
  mapValues(parameters, (value, name) => {
    const definitions = parameterDefinitions[name]
    if (typeof definitions === 'undefined') {
      return value
    }

    const defaultValue = definitions.defaultValue

    switch (definitions.type) {
      case string:
        return typeof value === 'undefined' || value === null
          ? defaultValue
          : value

      case number: {
        const valueAsInt = parseInt(value, 10)
        return isNaN(valueAsInt) ? defaultValue : valueAsInt
      }

      default:
        return value
    }
  })

export const mapLayoutToWidgetParameterUpserts = (layout, widgets) =>
  layout.map((cell) => {
    const widget = widgets.find((w) => w.id === cell.i)
    if (!widget) {
      return null
    }

    const parameters = {
      x: cell.x === widget.parameters.get('x') ? undefined : cell.x,
      y: cell.y === widget.parameters.get('y') ? undefined : cell.y,
      width: cell.w === widget.parameters.get('width') ? undefined : cell.w,
      height: cell.h === widget.parameters.get('height') ? undefined : cell.h,
    }

    const cleaned = omitBy(parameters, isUndefined)

    return {
      dashboardId: widget.dashboardId,
      widgetId: widget.id,
      parameters: cleaned,
    }
  })

const getUrlPrefix = (companyCode, customerCode) => {
  if (customerCode && !companyCode) {
    return `/${customerCode}/dashboards`
  }
  if (companyCode) {
    return `/${customerCode}/${companyCode}/dashboards`
  }
  return '/dashboards'
}

export const DASHBOARD_CATEGORY = {
  SYSTEM: 'SYSTEM',
  CUSTOMER: 'CUSTOMER',
  SHARED_WITH_ME: 'SHARED_WITH_ME',
  COLLABORATION: 'COLLABORATION',
  MY_DASHBOARDS: 'MY_DASHBOARDS',
}

/**
 * Determines which display category a dashboard belongs to.
 * Works with both plain JS objects (dashboardUsers/dashboardRoles as arrays)
 * and Immutable Records (as Immutable Lists) — editor counting falls back from
 * `.size` to `.length` so neither shape is mis-categorized.
 */
export const getDashboardCategory = (dashboard) => {
  if (dashboard?.isSystemDashboard) return DASHBOARD_CATEGORY.SYSTEM
  if (dashboard?.isCustomerDashboard) return DASHBOARD_CATEGORY.CUSTOMER
  if (!dashboard?.canEdit) return DASHBOARD_CATEGORY.SHARED_WITH_ME
  const countEditors = (collection) => {
    const editors = collection?.filter((entry) => entry.canEdit)
    return editors?.size ?? editors?.length ?? 0
  }
  const editorsCount =
    countEditors(dashboard?.dashboardUsers) +
    countEditors(dashboard?.dashboardRoles)
  if (editorsCount > 1) return DASHBOARD_CATEGORY.COLLABORATION
  return DASHBOARD_CATEGORY.MY_DASHBOARDS
}

/**
 * Returns the dashboard we should auto-navigate to after a create/copy, or
 * undefined when we shouldn't navigate yet. Gated on createdDashboardName only
 * — the inline rename input uses a separate state, so this never fires while
 * renaming.
 */
export const getCreatedDashboardToNavigate = ({
  createdDashboardName,
  dashboards,
  loading,
  error,
}) => {
  if (!createdDashboardName || !(dashboards?.size > 0) || loading || error) {
    return undefined
  }
  return dashboards.find((dashboard) => dashboard.name === createdDashboardName)
}

export const parseLocation = ({ companyCode, customerCode, id }) => {
  let base = getUrlPrefix(companyCode, customerCode)
  if (id) {
    return `${base}/${id}`
  }
  return base
}
